/**
 * Roadmap — /roadmap
 * ------------------
 * Unlisted, passcode-gated planning page. Renders the shared RoadmapBoard
 * component (which is also mounted inside NEXA → Life → Business → Levanta,
 * both hitting the same MongoDB collection so edits sync in both directions).
 *
 *  • Viewer passcode  → read-only view of the full roadmap.
 *  • Admin passcode   → same page with inline add / edit / delete and
 *    live todo check-offs.
 *
 * Passcode travels in the X-Roadmap-Key header (sessionStorage).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, LogOut, ShieldCheck } from 'lucide-react';
import RoadmapBoard from './roadmap/RoadmapBoard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'levanta_roadmap_key';

const makeApi = (key) => async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}/api/roadmap${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'X-Roadmap-Key': key,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/* ---------------------------------------------------------------- */
/*  Passcode gate                                                   */
/* ---------------------------------------------------------------- */
const Gate = ({ onUnlock }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim() || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/roadmap/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: value.trim() }),
      });
      if (!res.ok) throw new Error('bad passcode');
      const { role } = await res.json();
      onUnlock(value.trim(), role);
    } catch {
      setError(true);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-[#eef1fb] to-white">
      <form
        onSubmit={submit}
        data-testid="roadmap-gate-form"
        className="relative z-10 w-full max-w-md rounded-3xl bg-white/95 backdrop-blur shadow-2xl ring-1 ring-black/5 px-8 py-10"
      >
        <div className="w-12 h-12 rounded-2xl bg-blue-500/15 flex items-center justify-center mb-5">
          <Lock className="w-5 h-5 text-blue-600" strokeWidth={2.2} />
        </div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-2">
          Unlisted
        </p>
        <h1 className="text-3xl font-black text-[#0b1f3a] tracking-tight">
          Levanta — roadmap
        </h1>
        <p className="mt-3 text-sm text-[#0b1f3a]/65 leading-relaxed">
          This page is shared by invitation. Enter the passcode you received.
        </p>
        <input
          type="password"
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(false); }}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          autoFocus
          aria-label="Passcode"
          data-testid="roadmap-gate-input"
          className={`mt-7 w-full px-4 py-3 rounded-xl bg-white ring-1 transition-colors text-[#0b1f3a] tracking-wider font-mono text-lg focus:outline-none ${
            error ? 'ring-red-400 focus:ring-red-500' : 'ring-black/10 focus:ring-blue-500'
          }`}
        />
        {error && (
          <p className="mt-3 text-xs text-red-600 font-medium" data-testid="roadmap-gate-error">
            That passcode didn&rsquo;t match.
          </p>
        )}
        <button
          type="submit"
          data-testid="roadmap-gate-submit"
          className="mt-6 w-full px-6 py-3 rounded-xl bg-[#0b1f3a] text-white font-semibold hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
          disabled={busy}
        >
          {busy ? 'Checking…' : 'Open the roadmap →'}
        </button>
      </form>
    </div>
  );
};

/* ---------------------------------------------------------------- */
/*  Page                                                            */
/* ---------------------------------------------------------------- */
export default function Roadmap() {
  const [auth, setAuth] = useState(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const isAdmin = auth?.role === 'admin';
  const api = useMemo(() => (auth ? makeApi(auth.key) : null), [auth]);

  /* Unlisted page hygiene: title + noindex. */
  useEffect(() => {
    document.title = 'Roadmap — Levanta';
    let tag = document.querySelector('meta[name="robots"]');
    const created = !tag;
    const prev = tag ? tag.getAttribute('content') : null;
    if (created) {
      tag = document.createElement('meta');
      tag.name = 'robots';
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (created) document.head.removeChild(tag);
      else tag.setAttribute('content', prev);
    };
  }, []);

  const unlock = (key, role) => {
    const next = { key, role };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    setAuth(next);
  };

  const signOut = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuth(null);
  }, []);

  if (!auth) return <Gate onUnlock={unlock} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#eef1fb] via-white to-[#f4f6fc]" data-testid="roadmap-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-black/5">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="text-lg font-black tracking-[0.18em] text-[#0b1f3a]" data-testid="roadmap-home-link">
            LEVANTA
          </Link>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <span className="hidden sm:flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 px-2.5 py-1 rounded-full" data-testid="roadmap-admin-badge">
                <ShieldCheck className="w-3.5 h-3.5" /> Edit mode
              </span>
            )}
            <button
              onClick={signOut}
              data-testid="roadmap-signout"
              className="flex items-center gap-1.5 text-sm font-semibold text-[#0b1f3a]/60 hover:text-[#0b1f3a] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 pb-28">
        <div className="pt-12">
          <RoadmapBoard
            api={api}
            isAdmin={isAdmin}
            onAuthLost={signOut}
            heroBlurb="Where we are, what we’re building next, and where this is all going."
          />
        </div>
      </main>
    </div>
  );
}
